import { useEffect, useState } from 'react';
import { cartMessageApi } from '../api/Api';

export function buildMessageSnapshot({ placement, countryCode, stateId, items, subtotal }) {
  const normalizedCountryCode = String(countryCode || '').trim().toUpperCase();
  const amount = Number(subtotal);
  if (!['cart', 'checkout'].includes(placement)
    || !/^[A-Z]{2,3}$/.test(normalizedCountryCode)
    || !Array.isArray(items) || !items.length || !Number.isFinite(amount)
    || amount < 0 || amount > 9999999999.99) return null;
  const quantities = new Map();
  for (const item of items) {
    const sku = Number(item.product_sku_id);
    const quantity = Number(item.quantity);
    if (!Number.isInteger(sku) || sku <= 0 || !Number.isInteger(quantity) || quantity < 1) return null;
    quantities.set(sku, (quantities.get(sku) || 0) + quantity);
    if (quantities.get(sku) > 10000) return null;
  }
  if (quantities.size > 100) return null;
  const state = stateId == null || stateId === '' ? null : Number(stateId);
  if (state !== null && (!Number.isInteger(state) || state <= 0)) return null;
  return {
    placement, country_code: normalizedCountryCode, state_id: state,
    cart_subtotal: Number(amount.toFixed(2)),
    items: [...quantities].sort(([a], [b]) => a - b)
      .map(([product_sku_id, quantity]) => ({ product_sku_id, quantity })),
  };
}

export function normalizeMessages(payload, placement) {
  if (payload?.success !== true || !Array.isArray(payload.data?.messages)) return [];
  const states = {
    shipping_address_required: ['action_required', 'info'],
    shipping_threshold_pending: ['pending', 'info'],
    shipping_free: ['eligible', 'success'],
  };
  return payload.data.messages.filter(message => message && message.source === 'shipping'
    && typeof message.id === 'string' && message.id.length > 0
    && message.placement === placement && typeof message.text === 'string' && message.text.trim()
    && states[message.code]?.[0] === message.state
    && states[message.code]?.[1] === message.tone).slice(0, 1);
}

export default function useCartMessages(context, refreshKey = '') {
  const snapshot = buildMessageSnapshot(context);
  const request = snapshot ? JSON.stringify(snapshot) : '';
  const key = JSON.stringify([request, refreshKey]);
  const [result, setResult] = useState({ key: '', messages: [] });

  if (result.key !== key) setResult({ key, messages: [] });

  useEffect(() => {
    if (!request) return undefined;
    const controller = new AbortController();
    let active = true;
    const timer = setTimeout(async () => {
      try {
        const payload = JSON.parse(request);
        const response = await cartMessageApi.evaluate(payload, controller.signal);
        if (active) setResult({ key, messages: normalizeMessages(response.data, payload.placement) });
      } catch (_) {
        if (active) setResult({ key, messages: [] });
      }
    }, 350);
    return () => {
      active = false;
      clearTimeout(timer);
      controller.abort();
    };
  }, [request, key]);

  // Hide previous results during render, before the next effect runs.
  return request && result.key === key ? result.messages : [];
}
